import React, { ChangeEventHandler, ReactElement, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconCrossCircle, Input, Typography, Wrapper } from '@screentone/core';

import { SortDropdown } from 'components';
import SearchContentFiltersContainer, {
  SearchContentFilters,
  ShowFilterOptions
} from 'components/search-content/components/search-content-filters/SearchContentFiltersContainer';
import { useDragAndDrop } from 'contexts/drag-and-drop/useDragAndDrop';
import { useScratchpadContext } from 'features/section-page-edit/components/scratch-pad/scratchpad-context';
import { useConvertedProperty } from 'hooks';
import { AllessehContent, AllessehQuerySortKeyTypes, AllessehQuerySortOrderType } from 'hooks/useAllessehContentQuery';
import { LC_CARD_URL_REGEX, URL_REGEX } from 'utils/url';
import SearchByQueryTerm from './components/search-by-query-term/searchByQueryTerm';
import SearchByUrl from './components/search-by-url/SearchByUrl';
import styles from './SearchContent.module.scss';

const TOP_CONTENT_HEIGHT = 350;

export interface SearchParams {
  search: string;
  filters: SearchContentFilters;
  sort: AllessehQuerySortKeyTypes;
  sortOrder?: AllessehQuerySortOrderType;
}

export interface SearchProps {
  defaultFilterState: SearchContentFilters;
  onSearchParamsChange: (changedParams: SearchParams) => void;
  renderContentCard: (content: AllessehContent, index: number, embargo?: boolean) => ReactElement;
  resultListClassName?: string;
  searchParams: SearchParams;
  showFilters?: ShowFilterOptions;
}

export interface SearchContentProps {
  searchProps: SearchProps;
  beforeContent?: React.ReactNode;
  embargo?: boolean;
}

const FILTER_MAPPING: { [key: string]: string[] } = {
  marketwatch: ['author', 'sectionType']
};

const getFiltersForDisplay = ({
  currentProperty,
  searchParams
}: {
  currentProperty: string | null;
  searchParams: SearchParams;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!!currentProperty && FILTER_MAPPING[currentProperty]) {
    const displayFilters: any[] = [];

    FILTER_MAPPING[currentProperty].forEach((filterKey) => {
      if (searchParams.filters[filterKey]) {
        displayFilters.push(searchParams.filters[filterKey]);
      }
    });

    const filterString = displayFilters.join(', ');
    return filterString;
  }

  return '';
};

export const SearchContent = ({ searchProps, beforeContent = null, embargo = false }: SearchContentProps) => {
  const {
    defaultFilterState,
    onSearchParamsChange,
    resultListClassName,
    renderContentCard,
    searchParams,
    showFilters
  } = searchProps;
  const [searchByUrl, setSearchByUrl] = useState(false);
  const { searchInserted, acknowledgeSearchInsert } = useDragAndDrop();

  useEffect(() => {
    if (searchInserted) {
      acknowledgeSearchInsert();
    }
  }, [searchInserted, acknowledgeSearchInsert]);

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const isUrl = URL_REGEX.test(e.target.value);
    // LC cards can't be searched by URL,
    // so we need to convert url into allessedId and searchByQueryTerm
    const lcCardUrl = e.target.value.match(LC_CARD_URL_REGEX);

    if (isUrl && !lcCardUrl) {
      setSearchByUrl(true);
    } else {
      setSearchByUrl(false);
    }

    let searchTerm = e.target.value;
    if (lcCardUrl) {
      // sample input:
      // https://www.wsj.com/livecoverage/trump-elon-musk-feud/card/trump-calls-musk-man-who-has-lost-his-mind-abc-reports-FEJV6XEA5nItKdtL2EoK
      const domain = lcCardUrl[1]; // e.g., 'wsj'
      const feed = lcCardUrl[2]; // e.g., 'trump-elon-musk-feud'
      const slug = lcCardUrl[3]; // e.g., 'FEJV6XEA5nItKdtL2EoK'
      searchTerm = `lc-${domain}-${feed}-${slug}`;
    }

    onSearchParamsChange({ ...searchParams, search: searchTerm });
  };

  const handleFiltersChange = (newFilters: Partial<SearchContentFilters>) => {
    onSearchParamsChange({ ...searchParams, filters: { ...searchParams.filters, ...newFilters } });
  };

  const handleSortChange = (newSort: AllessehQuerySortKeyTypes) => {
    onSearchParamsChange({ ...searchParams, sort: newSort });
  };

  const handleClearSearch = () => {
    onSearchParamsChange({ ...searchParams, search: '' });
    setSearchByUrl(false);
  };

  const currentProperty = useConvertedProperty();

  const scratchpadCtx = useScratchpadContext();

  const searchContentByProps = useMemo(() => ({
    searchParams,
    resultListClassName,
    renderContentCard,
    scratchpadItemIds: scratchpadCtx?.getAllScratchpadItemIds()
  }), [searchParams, resultListClassName, renderContentCard, scratchpadCtx]);

  const filterString = getFiltersForDisplay({ currentProperty, searchParams });

  return (
    <>
      {!embargo && (
        <Box
          data-testid="search-content-container"
          padding={{ all: 'sm' }}
          margin={{ top: 'sm' }}
          className={styles.searchBox}
        >
          <div data-testid="search-content-filter" className={styles.searchInput}>
            <Input
              data-testid="page-search-content-input"
              type="text"
              placeholder="Search"
              value={searchParams.search}
              onChange={handleSearchChange}
              margin={{ right: 'sm' }}
            />

            <Button
              tertiary
              icon={IconCrossCircle}
              onClick={handleClearSearch}
              disabled={!searchParams.search}
              aria-label="Clear search"
            />
            <SortDropdown sortEnum={AllessehQuerySortKeyTypes} value={searchParams.sort} onChange={handleSortChange} />
          </div>
        </Box>
      )}
      {!embargo && (
        <SearchContentFiltersContainer
          defaultFilterState={defaultFilterState}
          filters={searchParams.filters}
          onChangeFilters={handleFiltersChange}
          showFilters={showFilters}
        />
      )}

      {filterString && <Typography variant="label3">Filters: {filterString}</Typography>}
      <Wrapper
        data-testid="search-content-search-by-container"
        className={styles.searchContentWrapper}
        style={{
          maxHeight: scratchpadCtx ? window.innerHeight - scratchpadCtx.height - TOP_CONTENT_HEIGHT : 'initial'
        }}
      >
        {beforeContent}
        {searchByUrl ? (
          <SearchByUrl {...searchContentByProps} embargo={embargo} />
        ) : (
          <SearchByQueryTerm {...searchContentByProps} embargo={embargo} />
        )}
      </Wrapper>
    </>
  );
};
